import { IObservableValue } from "mobx";
import { observer } from "mobx-react";
import * as React from "react";
import styled from "styled-components";
import { CvpQueryClientProvider } from "../api/queryClient";
import { AdminProvider } from "../business-components/adminSettings/AdminContext";
import { Layout } from "../business-components/layout";
import { Routes } from "../business-components/Routes";
import { AppType } from "../constants/AppType";
import { UnsavedChangesHandler } from "../generic-components/common/FormikUnsavedChangesBlocker";
import { CvpToast } from "../generic-components/messages/CvpToastMessages";
import { getAppType } from "../getAppType";
import { TranslationProvider, useLocStore } from "../localization";
import { LocContext } from "../localization/LocContext";
import { usePrimeLocale } from "../localization/usePrimeLocale";
import { CvpSettings } from "../settings/CvpSettings";
import { useSettings } from "../settings/useSettings";
import { ThemeProvider } from "../themes/generated";
import { useRestoreConcernAfterHardcodedLinkClick } from "../url/useRestoreConcernAfterHardcodedLinkClick";
import { PluginComponentRenderer } from "../utils/PluginComponents";
import { concatClassnames } from "../utils/util";
import { AppGlobalStyles, UserProvidedStyle } from "./AppGlobalStyles";
import { AppStyleProps, getThemeSettingValue } from "./getThemeSettingValue";

export interface CVPortalProps {
  settings: IObservableValue<CvpSettings>;
}

export const App = ({ settings }: CVPortalProps) => {
  return (
    <React.Suspense fallback={null}>
      <AdminProvider>
        <CvpQueryClientProvider>
          <UnsavedChangesHandler>
            <AppContainerInner />
            <TranslationProvider />
          </UnsavedChangesHandler>
        </CvpQueryClientProvider>
      </AdminProvider>
    </React.Suspense>
  );
};

const AppContainerInner = observer(() => {
  const settings = useSettings();
  usePrimeLocale();

  // prefetch
  useLocStore(LocContext.GENERAL);

  useRestoreConcernAfterHardcodedLinkClick();

  return (
    <ThemeProvider
      // theme="lara-light-indigo"
      theme={settings.theme}
      replaceColors={
        // use original stylesheet if primaryColor is unchanged
        settings.primaryColor !== "var(--primary-color)"
      }
    >
      <AppGlobalStyles />
      <UserProvidedStyle css={settings.css} />

      <AppStyles
        className={concatClassnames([
          "p-component",
          "cvp-app-container",
          getAppType() === AppType.iframe && "cvp-app-container-iframe",
          getAppType() === AppType.cvWebtempNo &&
            "cvp-app-container-cvwebtempno",
        ])}
        fontSize={settings.fontSize}
        backgroundColor={settings.backgroundColor}
        primaryColor={settings.primaryColor}
        themeName={settings.theme}
        appType={getAppType()}
      >
        <Layout>
          <React.Suspense fallback={null}>
            <CvpToast />
            <PluginComponentRenderer />
            <Routes />
          </React.Suspense>
        </Layout>
      </AppStyles>
    </ThemeProvider>
  );
});

const AppStyles = styled.div<AppStyleProps>`
  &.cvp-app-container {
    color: var(--text-color);
    box-sizing: border-box;

    &.cvp-app-container-cvwebtempno {
      min-height: calc(100 * var(--vh));
    }

    &:not(.cvp-app-container-cvwebtempno) {
      /* width: calc(min(var(--standard-cvp-width), 100%) - 2rem);
      max-width: calc(min(var(--standard-cvp-width), 100%) - 2rem);
      margin-left: auto;
      margin-right: auto;
      margin-top: 0;
      margin-bottom: 0;
      padding-top: 1rem;
      padding-bottom: 1rem; */
    }

    ${(p) => getThemeSettingValue(p, "backgroundColor")};
    ${(p) => getThemeSettingValue(p, "fontSize")};

    ${(p) => getThemeSettingValue(p, "fontFamily")};

    .p-component,
    .p-inputtext {
      ${(p) => getThemeSettingValue(p, "fontFamily")};
    }

    /* Undo special css in customer pages */
    mark {
      padding: 0;
      background: yellow;
    }
  }

  .hidden {
    display: none;
  }

  .p-link {
    color: var(--text-color);
  }
`;
